/**
 * SystemStatus (BM-H314) — production state, legible inside the app.
 *
 * Route: /admin/system-status. Menu: avatar menu, "System Status" (directors
 * and admins only).
 *
 * Why this page exists: every endpoint that reports production state sits
 * behind require_session, and the session is a bearer token the SPA attaches
 * per request. Typing an API URL into a browser address bar therefore always
 * returns SESSION_EXPIRED. It is not a bug and it is not fixable from the
 * browser. The answer is that production state has to be readable in the app,
 * which is what this page is.
 *
 * Principles, deliberately narrow:
 *   - VALUES, NOT VERDICTS. Every field is printed exactly as production
 *     returned it. No green ticks computed from an assumption.
 *   - A FAILED READ SAYS SO. Amber "could not be read", never a fall through
 *     to a healthy-looking blank.
 *   - REUSE. /api/system/email-status, /api/admin/self-diagnostics,
 *     /api/system/build-info and /api/system/bugbot-diag already exist.
 *     No new probes.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Loader2, AlertTriangle, Mail, Server, Activity, Bug,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { useDirectorRole } from "../hooks/useDirectorRole";

export const EMAIL_GATE_FIELDS = [
  ["emails_enabled", "emails_enabled"],
  ["misconfigured", "misconfigured"],
  ["missing_credentials", "missing_credentials"],
  ["mailbox", "mailbox"],
  ["mailbox_reachable", "mailbox_reachable"],
  ["mailbox_detail", "mailbox_detail"],
  ["reason", "reason"],
  ["checked_at", "checked_at"],
];

export const BUGBOT_DIAG_FIELDS = [
  ["created_at_utc", "created_at_utc"],
  ["exception_type", "exception_type"],
  ["context", "context"],
  ["conversation_id", "conversation_id"],
  ["user_id", "user_id"],
  ["channel", "channel"],
];

export const BUILD_FIELDS = [
  ["git_commit", "git_commit"],
  ["commit_source", "commit_source"],
  ["built_at_utc", "built_at_utc"],
  ["process_started_at", "process_started_at (last recycle)"],
  ["uptime_seconds", "uptime_seconds"],
  ["bundle_sha256", "bundle_sha256"],
  ["python_version", "python_version"],
  ["site_name", "site_name"],
  ["instance_id", "instance_id"],
];

export function formatValue(value) {
  if (value === true) return "true";
  if (value === false) return "false";
  if (value === null || value === undefined || value === "") return "not returned";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "none";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function userEmail() {
  return (
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    ""
  );
}

function headers() {
  const email = userEmail();
  return email ? { "X-Bonsai-User-Id": email, "X-User-Email": email } : {};
}

function UnknownBlock({ testid, label, detail }) {
  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
      data-testid={testid}
      role="status"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <span className="font-semibold">{label} could not be read.</span>{" "}
        That means unknown. It is not evidence that this is healthy, and not
        evidence that it is broken.
        {detail ? ` (${detail})` : ""}
      </div>
    </div>
  );
}

function ValueTable({ fields, data, prefix }) {
  return (
    <table className="w-full text-sm" data-testid={`${prefix}-values`}>
      <tbody>
        {fields.map(([key, label]) => (
          <tr key={key} className="border-b border-white/5 last:border-0">
            <th className="text-left font-normal text-gray-400 py-1.5 pr-4 align-top w-1/2">
              {label}
            </th>
            <td
              className="py-1.5 font-mono text-gray-100 break-all"
              data-testid={`${prefix}-${key}`}
            >
              {formatValue(data[key])}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function useReader(path) {
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    setError("");
    try {
      const res = await axios.get(`${API}${path}`, { headers: headers() });
      if (res?.data && typeof res.data === "object") {
        setData(res.data);
        setState("read");
      } else {
        setData(null);
        setState("unreadable");
        setError("the endpoint answered with no body");
      }
    } catch (err) {
      setData(null);
      setState("unreadable");
      setError(errorMessage(err, "the read failed"));
    }
  }, [path]);

  useEffect(() => { load(); }, [load]);
  return { state, data, error, load };
}

export default function SystemStatus() {
  const navigate = useNavigate();
  const { isDirector, loading: roleLoading } = useDirectorRole();

  const email = useReader("/system/email-status");
  const build = useReader("/system/build-info");
  const diag = useReader("/admin/self-diagnostics");
  const bugbotDiag = useReader("/system/bugbot-diag?limit=20");
  const [readAt, setReadAt] = useState(new Date().toISOString());

  const refreshAll = () => {
    email.load();
    build.load();
    diag.load();
    bugbotDiag.load();
    setReadAt(new Date().toISOString());
  };

  if (roleLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking your role...
      </div>
    );
  }

  if (!isDirector) {
    return (
      <div className="p-8" data-testid="system-status-forbidden">
        <h1 className="text-lg font-bold text-teal mb-2">System Status</h1>
        <p className="text-sm text-gray-400">
          This page is available to directors and administrators.
        </p>
      </div>
    );
  }

  const diagChecks =
    diag.state === "read" && diag.data?.checks && typeof diag.data.checks === "object"
      ? Object.entries(diag.data.checks)
      : [];

  const tracebacks =
    bugbotDiag.state === "read" && Array.isArray(bugbotDiag.data?.tracebacks)
      ? bugbotDiag.data.tracebacks
      : [];

  return (
    <div className="p-6 space-y-6 max-w-3xl" data-testid="system-status-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <h1 className="text-base font-bold text-teal">System Status</h1>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto gap-1"
          onClick={refreshAll}
          data-testid="system-status-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      <p className="text-xs text-gray-400">
        Values exactly as production returned them. Nothing on this page is a
        computed verdict. Where a read fails it says so, because unknown is not
        the same as healthy. Read by this browser at{" "}
        <span data-testid="system-status-read-at">{readAt}</span>.
      </p>

      <section className="glass-card p-6 rounded-md" data-testid="email-gate-section">
        <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4" /> Email send gate
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          <code>GET /api/system/email-status</code>
        </p>
        {email.state === "loading" && (
          <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="email-gate-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading...
          </div>
        )}
        {email.state === "unreadable" && (
          <UnknownBlock testid="email-gate-unknown" label="The email gate" detail={email.error} />
        )}
        {email.state === "read" && email.data && (
          <ValueTable fields={EMAIL_GATE_FIELDS} data={email.data} prefix="email-gate" />
        )}
      </section>

      <section className="glass-card p-6 rounded-md" data-testid="build-section">
        <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
          <Server className="w-4 h-4" /> Backend build serving now
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          <code>GET /api/system/build-info</code>. The commit comes from the
          stamp the deploy wrote into this artefact.{" "}
          <span className="text-gray-400">
            process_started_at is the authoritative answer to "has it recycled
            yet": if it is older than your merge, the new build is not serving.
          </span>
        </p>
        {build.state === "loading" && (
          <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="build-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading...
          </div>
        )}
        {build.state === "unreadable" && (
          <UnknownBlock testid="build-unknown" label="The backend build" detail={build.error} />
        )}
        {build.state === "read" && build.data && (
          <ValueTable fields={BUILD_FIELDS} data={build.data} prefix="build" />
        )}
      </section>

      <section className="glass-card p-6 rounded-md" data-testid="integrations-section">
        <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4" /> Integration checks at last boot
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          <code>GET /api/admin/self-diagnostics</code>. These are the checks the
          backend ran when it last started, including SharePoint, Graph
          credentials, Mongo, the scheduler and migrations. They are a snapshot
          from boot time, not a live probe.
        </p>
        {diag.state === "loading" && (
          <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="diag-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading...
          </div>
        )}
        {diag.state === "unreadable" && (
          <UnknownBlock testid="diag-unknown" label="The boot diagnostics" detail={diag.error} />
        )}
        {diag.state === "read" && diag.data && (
          <>
            <table className="w-full text-sm mb-3" data-testid="diag-values">
              <tbody>
                <tr className="border-b border-white/5">
                  <th className="text-left font-normal text-gray-400 py-1.5 pr-4 w-1/2">status</th>
                  <td className="py-1.5 font-mono text-gray-100" data-testid="diag-status">
                    {formatValue(diag.data.status)}
                  </td>
                </tr>
                <tr>
                  <th className="text-left font-normal text-gray-400 py-1.5 pr-4 w-1/2">timestamp</th>
                  <td className="py-1.5 font-mono text-gray-100" data-testid="diag-timestamp">
                    {formatValue(diag.data.timestamp)}
                  </td>
                </tr>
              </tbody>
            </table>
            {diagChecks.length === 0 ? (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="diag-no-checks">
                The snapshot carried no checks. That is unknown, not healthy.
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="diag-checks">
                <tbody>
                  {diagChecks.map(([name, value]) => (
                    <tr key={name} className="border-b border-white/5 last:border-0">
                      <th className="text-left font-normal text-gray-400 py-1.5 pr-4 align-top w-1/3">
                        {name}
                      </th>
                      <td
                        className="py-1.5 font-mono text-gray-100 break-all"
                        data-testid={`diag-check-${name}`}
                      >
                        {formatValue(value?.status)}
                        {value?.detail ? ` — ${formatValue(value.detail)}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>

      <section className="glass-card p-6 rounded-md" data-testid="bugbot-diag-section">
        <h2 className="text-sm font-semibold text-teal uppercase tracking-wider mb-4 flex items-center gap-2">
          <Bug className="w-4 h-4" /> Bug-filing crash tracebacks
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          <code>GET /api/system/bugbot-diag?limit=20</code>. Captured
          crash/diagnostic tracebacks from the last 7 days (secrets already
          scrubbed server-side), including phantom Teams confirm-card taps
          that found no staged pending action. Previously only reachable via
          Datadog, which requires admin permissions.
        </p>
        {bugbotDiag.state === "loading" && (
          <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="bugbot-diag-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading...
          </div>
        )}
        {bugbotDiag.state === "unreadable" && (
          <UnknownBlock testid="bugbot-diag-unknown" label="The bugbot diagnostics" detail={bugbotDiag.error} />
        )}
        {bugbotDiag.state === "read" && bugbotDiag.data && (
          <>
            {tracebacks.length === 0 ? (
              <p
                className="text-sm text-gray-400"
                data-testid="bugbot-diag-empty"
              >
                No captured tracebacks in the last 7 days.
              </p>
            ) : (
              <div className="space-y-3" data-testid="bugbot-diag-list">
                {tracebacks.map((tb, idx) => (
                  <details
                    key={tb.id || idx}
                    className="rounded-md border border-white/10 px-4 py-3"
                    data-testid={`bugbot-diag-row-${tb.id || idx}`}
                  >
                    <summary className="cursor-pointer text-sm text-gray-100">
                      <span className="font-mono text-gray-300">
                        {formatValue(tb.created_at_utc)}
                      </span>{" "}
                      <span className="font-semibold text-teal">
                        {formatValue(tb.exception_type)}
                      </span>
                    </summary>
                    <table className="w-full text-sm mt-3" data-testid={`bugbot-diag-fields-${tb.id || idx}`}>
                      <tbody>
                        {BUGBOT_DIAG_FIELDS.map(([key, label]) => (
                          <tr key={key} className="border-b border-white/5 last:border-0">
                            <th className="text-left font-normal text-gray-400 py-1.5 pr-4 align-top w-1/3">
                              {label}
                            </th>
                            <td
                              className="py-1.5 font-mono text-gray-100 break-all"
                              data-testid={`bugbot-diag-${key}-${tb.id || idx}`}
                            >
                              {formatValue(tb[key])}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <pre
                      className="mt-3 max-h-64 overflow-auto rounded-md bg-black/30 p-3 text-xs text-gray-300 whitespace-pre-wrap break-all"
                      data-testid={`bugbot-diag-traceback-${tb.id || idx}`}
                    >
                      {formatValue(tb.traceback)}
                    </pre>
                  </details>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
